import { Button, Grid, Group, Image, Loader, LoadingOverlay, Modal, Paper, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { FaEdit, FaPlus } from 'react-icons/fa';
import InputField, { InputFieldTypes } from '../components/input_field';
import { useLanguage } from '../contexts/language.context';
import { CREATE_CAR, GET_ALL_CARS, UPDATE_CAR } from '../services/cars';
import { carColors, parseKeyword } from '../utility/util';
import { GET_ALL_MODELS } from '../services/models';

export default function CarsPage() {
    const [models, setModels] = useState<any[]>([]);
    const [filters, setFilters] = useState({
        status: "all",
        search: "",
    })
    const [cars, setCars] = useState<any[]>([]);
    const [carModal, setCarModal] = useState<any>(null);
    const [loadingSaveCar, setLoadingSaveCar] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(false);

    const { str } = useLanguage();

    const shownItems = cars.filter(item => (
        [
            parseKeyword(item.license_plate ?? ""),
            parseKeyword(item.model?.brand?.title ?? ""),
            parseKeyword(item.model?.title ?? ""),
            parseKeyword(item.category?.title ?? ""),
            parseKeyword(carColors.find(cc => cc.value === item.color)?.label),
            parseKeyword(item.year ?? ""),
        ].join(",").includes(parseKeyword(filters.search))
    ))

    const saveCar = () => {
        const car = carModal;
        if (!car._id) {
            setLoadingSaveCar(true);
            CREATE_CAR(car)
                .then(res => {
                    loadData();
                    setCarModal(null);
                })
                .catch(err => { showNotification({ message: err.message, color: "red" }) })
                .finally(() => { setLoadingSaveCar(false); })
        } else {
            setLoadingSaveCar(true);
            let params = { ...car };
            delete params._id;
            UPDATE_CAR(car._id, params)
                .then(res => {
                    loadData();
                    setCarModal(null);
                })
                .catch(err => { showNotification({ message: err.message, color: "red" }) })
                .finally(() => { setLoadingSaveCar(false); })
        }
    }

    const loadData = async () => {
        try {
            setLoadingData(true);
            setModels(await GET_ALL_MODELS());
            setCars(await GET_ALL_CARS());
        } catch (err: any) {
            showNotification({ message: err.message, color: "red" })
        } finally {
            setLoadingData(false);
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    return <>
        <Grid>
            <Grid.Col span={{ md: 3 }}>
                <InputField
                    name="search"
                    placeholder="Buscar..."
                    value={filters.search}
                    onChange={(dt: any) => setFilters(f => ({ ...f, ...dt }))}
                />
            </Grid.Col>
            <Grid.Col span={{ md: 3 }}>
                <InputField
                    fieldType={InputFieldTypes.SELECT}
                    name="status"
                    clearable={false}
                    searchable={false}
                    value={filters.status}
                    onChange={(dt: any) => setFilters(f => ({ ...f, ...dt }))}
                    options={[
                        { label: "Todos", value: "all" },
                        { label: "Alugados", value: "rent" },
                        { label: "Em Manutenção", value: "maintenance" },
                    ]}
                />
            </Grid.Col>
            <Grid.Col span={{ md: 4 }}></Grid.Col>
            <Grid.Col span={{ md: 2 }}>
                <Button fullWidth loading={loadingData} onClick={() => setCarModal({})} leftSection={<FaPlus />}>Cadastrar</Button>
            </Grid.Col>
            <Grid.Col span={{ md: 12 }}>
                <Grid>
                    {shownItems.map(item => {
                        const carColor = carColors.find(cc => cc.value === item.color);
                        const image = (item.model?.colors ?? []).find((img: any) => img.color === carColor?.value);
                        return <Grid.Col span={{ md: 3 }}>
                            <Paper style={{ backgroundColor: '#EFEFEF' }}>
                                <Stack gap={10} p="md">
                                    <Text ta="center" fw="bold" size="lg">{item.license_plate}</Text>
                                    <Text lineClamp={3} h={80} ta="center">{[
                                        item.model?.brand?.title,
                                        item.model?.title,
                                        item.model?.category?.title,
                                        carColor?.label,
                                        item.year,
                                    ].join(", ")}</Text>
                                    <Image
                                        src={image?.image}
                                        height={120}
                                    />
                                </Stack>
                                <Button fullWidth leftSection={<FaEdit />} onClick={() => setCarModal(item)}>
                                    Editar
                                </Button>
                            </Paper>
                        </Grid.Col>
                    })}
                </Grid>
            </Grid.Col>
        </Grid>
        <Modal
            opened={!!carModal}
            onClose={() => setCarModal(null)}
            size="xl"
        >
            <Stack>
                <InputField
                    name="model"
                    value={carModal?.model?._id ?? carModal?.model}
                    title="Modelo"
                    onChange={({ model }: any) => setCarModal((pv: any) => ({ ...pv, model }))}
                    fieldType={InputFieldTypes.SELECT}
                    options={models.map(m => ({ label: m.title, value: m._id }))}
                />
                <InputField
                    name="color"
                    value={carModal?.color}
                    title="Cor"
                    onChange={({ color }: any) => setCarModal((pv: any) => ({ ...pv, color }))}
                    fieldType={InputFieldTypes.SELECT}
                    options={carColors}
                />
                <InputField
                    name="year"
                    value={carModal?.year}
                    title="Ano"
                    onChange={({ year }: any) => setCarModal((pv: any) => ({ ...pv, year }))}
                />
                <InputField
                    name="model_year"
                    value={carModal?.model_year}
                    title="Ano do Modelo"
                    onChange={({ model_year }: any) => setCarModal((pv: any) => ({ ...pv, model_year }))}
                />
                <InputField
                    name="license_plate"
                    value={carModal?.license_plate}
                    title="Placa"
                    onChange={({ license_plate }: any) => setCarModal((pv: any) => ({ ...pv, license_plate }))}
                />
                <InputField
                    name="register_number"
                    value={carModal?.register_number}
                    title="RENAVAM"
                    onChange={({ register_number }: any) => setCarModal((pv: any) => ({ ...pv, register_number }))}
                />
                <Group justify='flex-end'>
                    <Button
                        onClick={saveCar}
                        loading={loadingSaveCar}
                        disabled={!carModal?.model || !carModal?.color || !carModal?.year || !carModal?.license_plate}
                    >Salvar</Button>
                </Group>
            </Stack>
        </Modal>
    </>
}
